export interface ServiceData {
  id: number;
  slug: string;
  name: string;
  shortName: string;
  description: string;
  priceFrom: number;
  priceTo?: number;
  priceLabel: string;
  visits: number;
  icon: string;
  emoji: string;
  urgency: "emergency" | "high" | "medium" | "low";
  category: "rodents" | "insects" | "wildlife" | "birds" | "specialist";
  symptoms: string[];
  treatment: string;
}

export const SERVICES: ServiceData[] = [
  {
    id: 1,
    slug: "rats",
    name: "Rat Control & Removal",
    shortName: "Rats",
    description:
      "Professional rat extermination using advanced baiting systems and proofing solutions. We eliminate infestations fast and prevent re-entry.",
    priceFrom: 180,
    priceTo: 260,
    priceLabel: "£180 – £260",
    visits: 2,
    icon: "🐀",
    emoji: "🐀",
    urgency: "emergency",
    category: "rodents",
    symptoms: [
      "Droppings along walls or in cupboards",
      "Gnaw marks on food packaging or cables",
      "Scratching noises at night",
      "Burrows in garden or under decking",
    ],
    treatment:
      "2-visit treatment with professional-grade rodenticide bait stations and entry-point proofing.",
  },
  {
    id: 2,
    slug: "mice",
    name: "Mice Control & Removal",
    shortName: "Mice",
    description:
      "Rapid mice elimination with targeted bait placement and thorough proofing to stop re-entry through even the smallest gaps.",
    priceFrom: 180,
    priceTo: 260,
    priceLabel: "£180 – £260",
    visits: 2,
    icon: "🐭",
    emoji: "🐭",
    urgency: "emergency",
    category: "rodents",
    symptoms: [
      "Small dark droppings (rice-grain sized)",
      "Gnaw marks on food, cables, or furniture",
      "Scratching in walls or ceilings",
      "Nesting material (shredded paper/fabric)",
    ],
    treatment:
      "2-visit treatment with bait stations placed in key activity areas and gap-sealing recommendations.",
  },
  {
    id: 3,
    slug: "cockroaches",
    name: "Cockroach Treatment",
    shortName: "Cockroaches",
    description:
      "Complete cockroach eradication using professional gel baits and residual insecticides, targeting all life stages.",
    priceFrom: 180,
    priceTo: 320,
    priceLabel: "£180 – £320",
    visits: 2,
    icon: "🪳",
    emoji: "🪳",
    urgency: "emergency",
    category: "insects",
    symptoms: [
      "Droppings resembling coffee grounds",
      "Musty, oily odour in kitchen areas",
      "Egg cases (oothecae) in dark corners",
      "Seeing cockroaches during daylight hours",
    ],
    treatment:
      "2-visit gel bait and insecticide treatment targeting harbourage areas, kitchen appliances, and drains.",
  },
  {
    id: 4,
    slug: "wasps",
    name: "Wasp Nest Removal",
    shortName: "Wasps",
    description:
      "Safe, same-day wasp nest treatment and removal. Our technicians neutralise nests quickly to protect your family.",
    priceFrom: 95,
    priceLabel: "£95",
    visits: 1,
    icon: "🐝",
    emoji: "🐝",
    urgency: "high",
    category: "insects",
    symptoms: [
      "Visible wasp nest in loft, shed, or eaves",
      "High wasp activity around a specific area",
      "Buzzing sounds from walls or ceilings",
      "Wasps entering through a small hole",
    ],
    treatment:
      "Single visit — nest located and treated with professional insecticide dust. Nest removed where accessible.",
  },
  {
    id: 5,
    slug: "bedbugs",
    name: "Bed Bug Treatment",
    shortName: "Bed Bugs",
    description:
      "Thorough bed bug elimination using heat treatment and residual insecticides, targeting all hiding spots.",
    priceFrom: 85,
    priceLabel: "From £85",
    visits: 1,
    icon: "🛏️",
    emoji: "🛏️",
    urgency: "high",
    category: "insects",
    symptoms: [
      "Itchy red bites on skin (often in lines)",
      "Tiny blood spots on bedding",
      "Dark spots (excrement) on mattress seams",
      "Sweet, musty smell in bedroom",
    ],
    treatment:
      "Professional insecticide spray targeting mattresses, bed frames, skirting boards, and furniture.",
  },
  {
    id: 6,
    slug: "ants",
    name: "Ant Control",
    shortName: "Ants",
    description:
      "Effective ant control targeting the colony source with professional gel baits and residual treatments.",
    priceFrom: 150,
    priceLabel: "From £150",
    visits: 1,
    icon: "🐜",
    emoji: "🐜",
    urgency: "medium",
    category: "insects",
    symptoms: [
      "Ant trails in kitchen or garden",
      "Ant nest mounds in lawn or paving",
      "Ants around food storage areas",
      "Flying ants (swarm season)",
    ],
    treatment:
      "Gel bait application to trails and nest entry points, plus residual insecticide barrier treatment.",
  },
  {
    id: 7,
    slug: "fleas",
    name: "Flea Treatment",
    shortName: "Fleas",
    description:
      "Complete flea eradication treating all affected rooms with professional insecticide and IGR (insect growth regulator).",
    priceFrom: 150,
    priceLabel: "From £150",
    visits: 1,
    icon: "🦟",
    emoji: "🦟",
    urgency: "medium",
    category: "insects",
    symptoms: [
      "Itchy bites around ankles and lower legs",
      "Pets scratching excessively",
      "Tiny jumping insects on carpets or furniture",
      "Flea dirt (black specks) in pet bedding",
    ],
    treatment:
      "Full room treatment with residual insecticide and IGR spray to break the flea life cycle.",
  },
  {
    id: 8,
    slug: "foxes",
    name: "Fox Deterrent & Control",
    shortName: "Foxes",
    description:
      "Humane fox deterrent solutions to protect your garden, property, and pets from fox damage.",
    priceFrom: 120,
    priceLabel: "£120",
    visits: 1,
    icon: "🦊",
    emoji: "🦊",
    urgency: "low",
    category: "wildlife",
    symptoms: [
      "Fox droppings in garden",
      "Disturbed bins or compost heaps",
      "Damage to lawns or flower beds",
      "Foxes denning under decking or sheds",
    ],
    treatment:
      "Humane repellent application and den-proofing recommendations to deter foxes from your property.",
  },
  {
    id: 9,
    slug: "squirrels",
    name: "Squirrel Control",
    shortName: "Squirrels",
    description:
      "Professional grey squirrel control to protect your loft and property from damage and fire risks.",
    priceFrom: 180,
    priceLabel: "£180",
    visits: 1,
    icon: "🐿️",
    emoji: "🐿️",
    urgency: "high",
    category: "wildlife",
    symptoms: [
      "Scratching or running sounds in loft",
      "Chewed cables, insulation, or woodwork",
      "Squirrel entry holes in fascia boards",
      "Droppings in loft space",
    ],
    treatment:
      "Trap placement and removal, followed by entry-point proofing to prevent re-entry.",
  },
  {
    id: 10,
    slug: "pigeons",
    name: "Pigeon & Bird Control",
    shortName: "Pigeons",
    description:
      "Effective bird deterrent systems including spikes, netting, and wire to protect buildings from pigeon damage.",
    priceFrom: 85,
    priceLabel: "From £85",
    visits: 1,
    icon: "🕊️",
    emoji: "🕊️",
    urgency: "medium",
    category: "birds",
    symptoms: [
      "Pigeon droppings on ledges or vehicles",
      "Nesting in gutters or roof spaces",
      "Noise and mess on balconies",
      "Blocked gutters from nesting material",
    ],
    treatment:
      "Installation of anti-roosting spikes, bird netting, or wire systems tailored to your building.",
  },
  {
    id: 11,
    slug: "moles",
    name: "Mole Control",
    shortName: "Moles",
    description:
      "Professional mole trapping and control to protect lawns, gardens, and sports turf from mole damage.",
    priceFrom: 150,
    priceLabel: "From £150",
    visits: 1,
    icon: "🦔",
    emoji: "🦔",
    urgency: "low",
    category: "wildlife",
    symptoms: [
      "Molehills appearing on lawn or garden",
      "Raised ridges across grass",
      "Subsidence in lawn areas",
      "Damage to plant roots",
    ],
    treatment:
      "Professional trapping programme with regular monitoring until the mole is removed.",
  },
  {
    id: 12,
    slug: "carpet-beetles",
    name: "Carpet Beetle Treatment",
    shortName: "Carpet Beetles",
    description:
      "Targeted carpet beetle treatment protecting your carpets, clothing, and soft furnishings from larvae damage.",
    priceFrom: 120,
    priceLabel: "From £120",
    visits: 1,
    icon: "🪲",
    emoji: "🪲",
    urgency: "medium",
    category: "insects",
    symptoms: [
      "Irregular holes in carpets or clothing",
      "Shed larvae skins (bristly, brown)",
      "Adult beetles near windows",
      "Damage to natural fibre items",
    ],
    treatment:
      "Residual insecticide treatment to affected areas including carpet edges, wardrobes, and loft spaces.",
  },
  {
    id: 13,
    slug: "cluster-flies",
    name: "Cluster Fly Treatment",
    shortName: "Cluster Flies",
    description:
      "Effective cluster fly control targeting overwintering aggregations in loft spaces and wall cavities.",
    priceFrom: 150,
    priceLabel: "From £150",
    visits: 1,
    icon: "🪰",
    emoji: "🪰",
    urgency: "low",
    category: "insects",
    symptoms: [
      "Large numbers of sluggish flies in autumn/winter",
      "Flies clustering in loft or upper rooms",
      "Flies emerging from walls on warm days",
      "Dead flies accumulating on windowsills",
    ],
    treatment:
      "Insecticide treatment of loft space and harbourage areas to eliminate overwintering clusters.",
  },
  {
    id: 14,
    slug: "silverfish",
    name: "Silverfish Control",
    shortName: "Silverfish",
    description:
      "Professional silverfish treatment targeting damp areas and eliminating infestations in bathrooms, kitchens, and basements.",
    priceFrom: 120,
    priceLabel: "From £120",
    visits: 1,
    icon: "🐟",
    emoji: "🐟",
    urgency: "low",
    category: "insects",
    symptoms: [
      "Silverfish seen in bathrooms or kitchens at night",
      "Yellow stains or scales on paper/fabric",
      "Damage to books, wallpaper, or food packaging",
      "Presence in damp, dark areas",
    ],
    treatment:
      "Residual insecticide treatment to affected rooms, focusing on damp areas and harbourage points.",
  },
  {
    id: 15,
    slug: "moths",
    name: "Moth Treatment",
    shortName: "Moths",
    description:
      "Complete moth eradication protecting your clothing, carpets, and soft furnishings from textile moth larvae.",
    priceFrom: 150,
    priceLabel: "From £150",
    visits: 1,
    icon: "🦋",
    emoji: "🦋",
    urgency: "medium",
    category: "insects",
    symptoms: [
      "Holes in wool, silk, or cashmere clothing",
      "Damage to carpets (especially under furniture)",
      "Silky webbing or cocoons in wardrobes",
      "Adult moths flying near light sources",
    ],
    treatment:
      "Insecticide spray treatment to wardrobes, carpets, and soft furnishings, plus pheromone trap placement.",
  },
];

// ─── SPECIALIST SERVICES ───────────────────────────────────────────────────
export const SPECIALIST_SERVICES: ServiceData[] = [
  {
    id: 16,
    slug: "heat-treatment",
    name: "Heat Treatment",
    shortName: "Heat Treatment",
    description:
      "Whole-room or whole-property heat treatment — the most effective method for eliminating bed bugs, fleas, and other insects at all life stages without chemicals.",
    priceFrom: 450,
    priceLabel: "From £450",
    visits: 1,
    icon: "🔥",
    emoji: "🔥",
    urgency: "high",
    category: "specialist",
    symptoms: [
      "Severe or recurring bed bug infestation",
      "Chemical-resistant insect populations",
      "Need for chemical-free treatment",
      "Large property requiring full eradication",
    ],
    treatment:
      "Specialist heat equipment raises room temperature to 56°C+, killing all insects and eggs at every life stage in a single treatment.",
  },
  {
    id: 17,
    slug: "proofing-services",
    name: "Pest Proofing Services",
    shortName: "Proofing",
    description:
      "Professional pest-proofing to seal entry points and prevent rodents, insects, and wildlife from accessing your property — the most cost-effective long-term solution.",
    priceFrom: 85,
    priceLabel: "From £85",
    visits: 1,
    icon: "🔒",
    emoji: "🔒",
    urgency: "medium",
    category: "specialist",
    symptoms: [
      "Recurring rodent or insect infestations",
      "Visible gaps around pipes, cables, or vents",
      "Gaps under doors or around window frames",
      "Previous pest activity with no proofing done",
    ],
    treatment:
      "Full inspection of entry points followed by professional sealing using wire mesh, expanding foam, door brushes, and specialist materials.",
  },
  {
    id: 18,
    slug: "manhole-flap-valve",
    name: "Manhole Flap Valve Installation",
    shortName: "Flap Valve",
    description:
      "Installation of a one-way rat flap valve inside your drain or manhole to permanently prevent rats from entering your property through the sewer system.",
    priceFrom: 150,
    priceLabel: "From £150",
    visits: 1,
    icon: "🔧",
    emoji: "🔧",
    urgency: "high",
    category: "specialist",
    symptoms: [
      "Rats entering through toilets or drains",
      "Rat activity near manholes or inspection chambers",
      "Evidence of rats in drainage system",
      "Recurring rat infestations despite treatment",
    ],
    treatment:
      "Non-return rat flap valve fitted inside the drain or manhole, allowing waste water to flow out but preventing rats from swimming upstream into your property.",
  },
  {
    id: 19,
    slug: "cctv-drain-survey",
    name: "CCTV Drain Survey",
    shortName: "CCTV Survey",
    description:
      "Professional CCTV drain survey to identify rat entry points, drain defects, and blockages — essential for properties with recurring rodent problems.",
    priceFrom: 120,
    priceLabel: "From £120",
    visits: 1,
    icon: "📷",
    emoji: "📷",
    urgency: "medium",
    category: "specialist",
    symptoms: [
      "Rats suspected to be entering via drains",
      "Recurring rat infestations with no obvious entry point",
      "Slow-draining or blocked drains",
      "Smell of sewage inside property",
    ],
    treatment:
      "CCTV camera inserted into drain system to produce a full video survey report identifying defects, blockages, and rat entry points.",
  },
  {
    id: 20,
    slug: "sanitation-disinfecting",
    name: "Sanitation & Disinfecting",
    shortName: "Sanitation",
    description:
      "Professional deep sanitation and disinfection service following pest infestations — eliminating bacteria, pathogens, and odours left by rodents, birds, or insects.",
    priceFrom: 150,
    priceLabel: "From £150",
    visits: 1,
    icon: "🧹",
    emoji: "🧹",
    urgency: "medium",
    category: "specialist",
    symptoms: [
      "Rodent droppings or urine contamination",
      "Bird fouling on surfaces or in loft spaces",
      "Odour remaining after pest removal",
      "Need for hygiene certification after infestation",
    ],
    treatment:
      "Professional-grade disinfectant fogging and surface treatment to eliminate bacteria, viruses, and pathogens associated with pest infestations.",
  },
];

export const ALL_SERVICES = [...SERVICES, ...SPECIALIST_SERVICES];

export const SERVICES_BY_SLUG = Object.fromEntries(
  ALL_SERVICES.map((s) => [s.slug, s])
);

export const EMERGENCY_SERVICES = SERVICES.filter(
  (s) => s.urgency === "emergency"
);

export const SERVICE_CATEGORIES = {
  rodents: SERVICES.filter((s) => s.category === "rodents"),
  insects: SERVICES.filter((s) => s.category === "insects"),
  wildlife: SERVICES.filter((s) => s.category === "wildlife"),
  birds: SERVICES.filter((s) => s.category === "birds"),
  specialist: SPECIALIST_SERVICES,
};
